import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
    eraseCookie,
    getCurrentUser,
    getJwtBody,
    setCookie,
    setCurrentUser,
    setJwtBody
} from "../utils/cookie.ts";
import { useFetch } from "@/composables/useFetch.js";
import { uri } from "@/constants/config.js";
import { jwtDecode } from "jwt-decode";

const PROMOTION_KEY = 'isPromotionChecked'
export const useUserStore = defineStore('user', () => {
    const currentUser = ref(getCurrentUser())
    const currentJwtBody = ref(getJwtBody())
    const currentUserRoles = ref(currentJwtBody.value?.selectedSpace?.SpaceRoles.map(x => x.role) ?? [])
    const isManager = computed(() => currentUserRoles.value[0] === "root" ? true : false)

    const loginError = ref()

    const showLog = ref(false)
    const updateError = ref()
    const processing = ref(false)
    const updateProcessing = ref(false)
    const resetPasswordSuccess = ref()
    const notificationCount = ref({ count: null, unread: null })
    const getIsPromotionChecked = () => (localStorage.getItem(PROMOTION_KEY) || "false") === 'true';
    const setIsPromotionChecked = (value) => localStorage.setItem(PROMOTION_KEY, value);

    async function findUsers(params) {
        try {
            const { data, error } = await useFetch(`${uri.admin.user.list}`, {
                method: "GET",
                params,
            });
            if (data) {
                return data.data
            }
        } catch (err) {
            console.log(err);
        } finally {
        }
    }


    // Function to generate the iat and exp timestamps
    function generateTokenPayload(expiryDurationInSeconds = 3600) {
        // Get the current time in seconds since the Unix epoch
        const iat = Math.floor(Date.now() / 1000);

        // Calculate the expiration time
        const exp = iat + expiryDurationInSeconds;

        // Build the payload object
        return {
            iat: iat,
            exp: exp
        };
    }


    function setUser(payload) {
        const jwtBody = jwtDecode(payload.token)
        const { exp: expiration } = payload.token_payload ?? generateTokenPayload()
        setCookie('utn', payload.token, expiration)
        currentJwtBody.value = jwtBody
        currentUser.value = jwtBody.profile
        loginError.value = null
        setCurrentUser(jwtBody.profile)
        setJwtBody(jwtBody)
        currentJwtBody.value = jwtBody

        currentUser.value = jwtBody.profile
        currentUserRoles.value = jwtBody.selectedSpace.SpaceRoles.map(x => x.role)
    }

    function setLogout() {
        currentUser.value = null
        currentJwtBody.value = null
        loginError.value = null
        localStorage.removeItem(PROMOTION_KEY)
        eraseCookie('utn');
        eraseCookie('uName')
        eraseCookie('uEmail')
    }

    async function signOut() {
        // const token = getCookie('utn')
        // const {data, error} = await useFetch(uri.api.user.logout, {body: {all_session: 1}});
        setLogout()
        setCurrentUser(null)
        setJwtBody(null)
        setLogout()
    }


    function setNotificationCount(payload) {
        notificationCount.value = payload
    }

    async function requestNotificationCount() {
        const result = await useFetch(uri.api.notification.count, { body: {} });
        setNotificationCount(result.data.data)
    }

    function setUpdateProcessing(payload) {
        updateProcessing.value = payload
        updateError.value = null
    }


    async function tryUpdateProfile(payload) {
        setUpdateProcessing(true);
        await updateProfileData(payload)
    }

    async function updateProfileData(payload) {
        clearUpdateError()
        setUpdateProcessing(true)
        const result = await useFetch(uri.api.user.profile.update, { body: payload });
        if (result.data) {
            if (result.data.result) {
                await updateProfileState()
            } else {
                setUpdateProcessing(false)
            }
        } else {
            setUpdateProcessing(false)
        }
    }

    async function updateProfileState() {
        const result = await useFetch(uri.api.user.profile.view, { body: {} });
        if (result.data.result) {
            Object.keys(result.data.data).forEach(key => {
                currentUser.value[key] = result.data.data[key]
            });
            setCurrentUser(currentUser.value)
        } else {
            setUpdateError("Error")
        }
        setUpdateProcessing(false)
    }

    function clearUpdateError() {
        updateError.value = null
    }

    function setUpdateError(payload) {
        updateError.value = payload
        currentUser.value = null
        processing.value = false
    }

    async function tryUpdatePassword(payload) {
        setUpdateProcessing(true);
        await updatePasswordData(payload)
    }

    async function updatePasswordData(payload) {
        clearUpdateError()
        setUpdateProcessing(true)
        const result = await useFetch(uri.api.user.password.update, { body: payload });
        if (result.data) {
            if (result.data.result) {
                await updateProfileState()
            } else {
                setUpdateProcessing(false)
            }
        } else {
            setUpdateProcessing(false)
        }

    }

    async function trySetPassword(payload) {
        setUpdateProcessing(true);
        await setPasswordData(payload)
    }

    async function setPasswordData(payload) {
        clearUpdateError()
        setUpdateProcessing(true)
        const result = await useFetch(uri.api.user.password.add, { body: payload });
        if (result.data) {
            if (result.data.result) {
                await updateProfileState()
            } else {
                setUpdateProcessing(false)
            }
        } else {
            setUpdateProcessing(false)
        }
    }

    async function setShowLog(payload) {
        showLog.value = payload
    }

    function can(roles) {
        if (!(roles instanceof Array)) roles = [roles]
        roles.push("root")
        return roles.map(x => currentUserRoles.value.includes(x)).filter(x => !!x).length > 0
    }

    return {
        can,
        signOut,
        currentUser,
        currentUserRoles,
        currentJwtBody,
        loginError,
        setUser,
        requestNotificationCount,
        setShowLog,
        showLog,
        processing,
        resetPasswordSuccess,
        notificationCount,
        tryUpdateProfile,
        tryUpdatePassword,
        trySetPassword,
        updateError,
        updateProcessing,
        getIsPromotionChecked,
        setIsPromotionChecked,
        isManager,
        findUsers
    }

})